import type { AccountAddress } from '../../../types';
import type { WalletSourceMetadata } from '../balance';

/**
 * Helper function to build or update sources array for summed balances.
 * Sources track which wallets/custodies contributed to a summed balance.
 */
export function addUniqueSources(
  existingSources: WalletSourceMetadata[],
  newBalance: Pick<
    { chain: string; walletAddress: string },
    'chain' | 'walletAddress'
  >,
  accountAddresses: AccountAddress[]
): WalletSourceMetadata[] {
  const accountAddr = accountAddresses.find(
    (a) =>
      a.chain === newBalance.chain && a.address === newBalance.walletAddress
  );

  if (!accountAddr) {
    return existingSources;
  }

  const walletId = accountAddr.walletId ?? undefined;

  // Build the source ID based on custody type
  let sourceId: WalletSourceMetadata['id'];
  if (accountAddr.custody === 'vault') {
    sourceId = 'vault';
  } else if (walletId) {
    sourceId = `wallet:${walletId}`;
  } else {
    // Fallback for addresses without wallet IDs
    return existingSources;
  }

  // Check if this source already exists
  const sourceExists = existingSources.some((s) => s.id === sourceId);
  if (sourceExists) {
    return existingSources;
  }

  return [
    ...existingSources,
    {
      id: sourceId,
      label: accountAddr.label ?? '',
      custody: accountAddr.custody,
      isImported: accountAddr.isImported,
      walletId,
    },
  ];
}
